import { ProductLabel } from "../../ui/product-label.tsx";
import { ChannelName } from "../../ui/channel.tsx";
import { ProhibitIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useEffect, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { number } from "../../format.ts";
import { INBOX_LABELS as I, PLATFORM } from "../../labels.ts";
import { Button, CopyButton, ErrorText, Kbd, Pill } from "../../ui.tsx";
import { Excerpt, PointList } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { useInbox, Review, DetailPanelShell } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, plan M7 step 7g).

/** Where a message goes: a mail draft, or the person's profile on the channel. */
function outreachLink(channel: string, address: string, subject: string | null, body: string) {
  if (channel === "email") return { href: `mailto:${address}?${new URLSearchParams({ ...(subject ? { subject } : {}), body }).toString().replace(/\+/g, "%20")}`, label: I.openInMail };
  if (channel === "x") return { href: address.startsWith("@") ? `https://x.com/${address.slice(1)}` : address, label: I.openProfile };
  return { href: address, label: I.openProfile };
}

/**
 * One-to-one messages the person sends from their own account (plan M4 step
 * 4c-1): each recipient with the agent's draft, a way to open it where it is
 * sent, and the person's decision.
 */
export function OutreachReview() {
  const { outreachId } = useParams({ strict: false }) as { outreachId: string };
  const { items, intent, done } = useInbox();
  const client = useQueryClient();
  const order = useQuery(q.outreachOrder(outreachId));
  const item = items.find((entry) => entry.kind === "outreach" && entry.id === outreachId);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>();
  const [declined, setDeclined] = useState<Record<string, boolean>>({});
  const [confirming, setConfirming] = useState(false);
  const firstRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setError(undefined); setDeclined({}); setConfirming(false); }, [outreachId]);
  useEffect(() => { if (order.data && intent === "approve") firstRef.current?.focus(); }, [intent, order.data, outreachId]);

  if (order.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!order.data) return <DetailPanelShell><ErrorText error={order.error} /></DetailPanelShell>;
  const data = order.data;
  const open = data.status === "approved" && data.action_status === "committed";
  const queued = data.messages.filter((m) => m.status === "queued");
  const decide = async (id: string, status: "sent" | "skipped", doNotContact: boolean) => {
    setBusy(id); setError(undefined);
    try {
      const next = await api.post<C.OutreachOrder>(`/api/outreach-messages/${encodeURIComponent(id)}/decide`, { status, do_not_contact: doNotContact });
      client.setQueryData(q.outreachOrder(outreachId).queryKey, next);
      if (!next.messages.some((m) => m.status === "queued") && queued.length > 0) done(I.outreachDone);
      else requestAnimationFrame(() => firstRef.current?.focus());
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  };
  const forget = async () => {
    setBusy("forget"); setError(undefined);
    try {
      await api.post(`/api/outreach-orders/${encodeURIComponent(data.id)}/forget`);
      done(I.recipientsCleared);
    } catch (cause) { setError(cause); }
    finally { setBusy(null); setConfirming(false); }
  };
  const channel = PLATFORM[data.channel] ?? I.outreachChannel[data.channel] ?? data.channel;
  return <Review label={I.detail} title={data.title}
    meta={<><Pill tone={open && queued.length ? "teal" : "gray"} status>{open && queued.length ? I.reason.outreach_ready : I.outreachDone}</Pill>{item && <ProductLabel product={item.product} />}</>}
    actions={confirming
      ? <><Button tone="danger" busy={busy === "forget"} disabled={busy !== null} onClick={() => void forget()}>{I.confirmForget}</Button><Button tone="ghost" disabled={busy !== null} onClick={() => setConfirming(false)}>{I.keep}</Button></>
      : data.messages.some((m) => !m.forgotten) && <Button tone="soft" disabled={busy !== null} onClick={() => setConfirming(true)}>{I.forgetRecipients}</Button>}>
    <Excerpt className="z-agent-text" lines={2}>{data.why}</Excerpt>
    <p className="z-small z-muted">{confirming ? I.forgetNote : data.channel === "linkedin" ? `${I.outreachNote} ${I.linkedinNote}` : I.outreachNote}</p>
    <dl className="z-props">
      <dt>{I.where}</dt><dd>{I.actionKind.outreach} · <ChannelName channel={data.channel}>{channel}</ChannelName> · {I.audience[data.audience] ?? data.audience}</dd>
      <dt>{I.tier}</dt><dd>{I.authorship[data.authorship] ?? data.authorship}</dd>
      {data.playbook && <><dt>{I.playbook}</dt><dd className="z-mono">{I.playbookVersion(data.playbook.id, data.playbook.version)}</dd></>}
      <dt>{I.measurement}</dt><dd>{I.mode[data.measurement.mode] ?? data.measurement.mode}{data.measurement.window_days ? <> · {I.windowDays(data.measurement.window_days)}</> : null}</dd>
      {data.acceptance.length > 0 && <><dt>{I.acceptance}</dt><dd><PointList items={data.acceptance} /></dd></>}
    </dl>
    <section className="z-flow">
      <h3>{I.messages} <span className="z-count">{number(data.messages.length)}</span></h3>
      {data.messages.map((m) => {
        const link = m.address && m.body ? outreachLink(data.channel, m.address, m.subject, m.body) : null;
        const first = m.id === queued[0]?.id;
        return <div key={m.id} className="z-review-item">
          <h4>{m.forgotten ? I.forgotten : m.name}</h4>
          <dl className="z-props">
            <dt>{I.status}</dt><dd><Pill tone={m.status === "queued" ? "teal" : "gray"} status>{I.messageStatus[m.status] ?? m.status}</Pill>{m.do_not_contact && <> · {I.doNotContactSet}</>}</dd>
            {m.address && <><dt>{I.where}</dt><dd className="z-mono">{m.address}</dd></>}
            {m.subject && <><dt>{I.subject}</dt><dd className="z-agent-text">{m.subject}</dd></>}
          </dl>
          {m.body && <Excerpt className="z-agent-text" lines={4}>{m.body}</Excerpt>}
          {m.body && <p>{link && <a href={link.href} target="_blank" rel="noreferrer noopener">{link.label}</a>} <CopyButton text={m.subject ? `${m.subject}\n\n${m.body}` : m.body} /></p>}
          {m.status === "queued" && open && <>
            <ul className="z-check"><li>
              <input id={`dnc-${m.id}`} type="checkbox" checked={declined[m.id] ?? false} disabled={busy !== null} onChange={(event) => setDeclined({ ...declined, [m.id]: event.target.checked })} />
              <label htmlFor={`dnc-${m.id}`}>{I.doNotContact}</label>
            </li></ul>
            <p><Button ref={first ? firstRef : undefined} tone="primary" busy={busy === m.id} disabled={busy !== null} onClick={() => void decide(m.id, "sent", declined[m.id] ?? false)}>{I.markSent}{first && <> <Kbd>A</Kbd></>}</Button> <Button tone="soft" disabled={busy !== null} onClick={() => void decide(m.id, "skipped", declined[m.id] ?? false)}>{I.skipMessage}</Button></p>
          </>}
          {m.status !== "queued" && !m.do_not_contact && !m.forgotten && <p><Button tone="ghost" size="sm" icon={<ProhibitIcon />} disabled={busy !== null} onClick={() => void decide(m.id, m.status as "sent" | "skipped", true)}>{I.recordDoNotContact}</Button></p>}
        </div>;
      })}
    </section>
    <ErrorText error={error} />
  </Review>;
}
